/**
 * FlowContext — deterministic UI router bridge.
 *
 * Security constraints (non-negotiable):
 *  - Actions NEVER fire autonomously — always gated behind explicit user approval
 *  - All intent objects are Zod-validated before storage or dispatch
 *  - Bridge methods proxy to existing useCRM actions, inheriting its auth/permission model
 *  - Flow has no direct backend access; it cannot bypass useCRM permission checks
 *  - OWASP LLM02 / LLM08 compliant: output handling + bounded agency
 */

import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import type { CSSProperties } from 'react';
import { computePanelAnchorStyle } from '@/lib/flowPanelAnchor';
import { useCRM } from '@/hooks/useCRM';
import { useAuth } from '@/context/AuthContext';
import { validateSuggestion, sanitizeFlowString, isValidContactId } from '@/lib/flowValidation';
import { emailDraftingService } from '@/services/emailDraftingService';
import type { FlowSuggestion, EmailDraft } from '@/lib/flowValidation';
import type { Contact, ContactStage } from '@/types/contacts';

export type FlowWidgetState = 'idle' | 'suggestion' | 'drafting';

interface FlowContextValue {
  widgetState: FlowWidgetState;
  isOpen: boolean;

  /** Set by {@link FlowInlineLauncher} so the panel can anchor under the orb (Tasks page). */
  inlineFlowAnchorRef: React.RefObject<HTMLElement | null>;

  /**
   * Measured synchronously when opening from {@link openWidgetWithInlineAnchor} so the first paint
   * matches the orb (avoids layout flash / jump).
   */
  panelAnchorSnapshot: CSSProperties | null;

  /** Current suggestion being shown (head of queue) */
  currentSuggestion: FlowSuggestion | null;
  /** Full pending queue */
  pendingSuggestions: FlowSuggestion[];
  suggestionsCount: number;

  currentDraft: EmailDraft | null;

  /** Open widget (shows first pending suggestion or stays idle) — floating orb */
  openWidget: () => void;
  /** Open from inline orb: captures anchor rect in the same event tick before paint */
  openWidgetWithInlineAnchor: () => void;
  closeWidget: () => void;

  /**
   * APPROVAL GATE — fires only after explicit user confirmation.
   * Routes through existing useCRM.updateStage (inherits auth checks).
   */
  approveSuggestion: () => Promise<void>;
  dismissSuggestion: () => void;

  /** Begin email drafting flow for a contact */
  requestDraft: (contact: Contact) => Promise<void>;
  isDraftLoading: boolean;
  /** Copy draft to clipboard — no direct send */
  approveDraft: () => void;
  dismissDraft: () => void;

  /** Read-only contact lookup for widget display */
  getContact: (id: string) => Contact | undefined;
}

const FlowContext = createContext<FlowContextValue | null>(null);

export function useFlow(): FlowContextValue {
  const ctx = useContext(FlowContext);
  if (!ctx) throw new Error('useFlow must be used inside FlowProvider');
  return ctx;
}

// ─── Ambient Suggestion Engine ──────────────────────────────────────────────
// Deterministic, read-only. Scans contacts for actionable patterns.
// All output validated through FlowSuggestionSchema before use.
function deriveAmbientSuggestions(contacts: Contact[]): FlowSuggestion[] {
  const now = Date.now();
  const raw: unknown[] = [];

  for (const c of contacts) {
    let daysSince: number | null = null;
    if (c.lastContactedAt) {
      const contactedMs = new Date(c.lastContactedAt).getTime();
      if (Number.isFinite(contactedMs)) {
        daysSince = Math.floor((now - contactedMs) / 86_400_000);
      }
    }

    // New contact, no touch in >7 days
    if (c.stage === 'new' && daysSince !== null && daysSince > 7) {
      raw.push({
        type: 'follow_up',
        contactId: c.id,
        contactName: sanitizeFlowString(c.name),
        reason: sanitizeFlowString(`No contact in ${daysSince} days — reach out to keep warm`),
        urgency: daysSince > 21 ? 'high' : 'medium',
      });
    }

    // Qualified + high-value + recent activity → push to Proposal
    if (
      c.stage === 'qualified' &&
      c.value &&
      c.value > 0 &&
      daysSince !== null &&
      daysSince <= 3
    ) {
      raw.push({
        type: 'move_stage',
        contactId: c.id,
        contactName: sanitizeFlowString(c.name),
        reason: sanitizeFlowString('Recently engaged high-value contact — ready for Proposal stage'),
        targetStage: 'proposal' as ContactStage,
        urgency: 'high',
      });
    }

    // Proposal stale >10 days
    if (c.stage === 'proposal' && daysSince !== null && daysSince > 10) {
      raw.push({
        type: 'follow_up',
        contactId: c.id,
        contactName: sanitizeFlowString(c.name),
        reason: sanitizeFlowString(`Proposal has been open ${daysSince} days — follow up now`),
        urgency: 'high',
      });
    }

    // Negotiation stale >5 days
    if (c.stage === 'negotiation' && daysSince !== null && daysSince > 5) {
      raw.push({
        type: 'high_priority',
        contactId: c.id,
        contactName: sanitizeFlowString(c.name),
        reason: sanitizeFlowString(`Negotiation stalled ${daysSince} days — re-engage to close`),
        urgency: 'high',
      });
    }
  }

  // Validate all raw output before use
  const validated = raw
    .map(validateSuggestion)
    .filter((s): s is FlowSuggestion => s !== null);

  // Sort by urgency and cap at 5
  const urgencyRank: Record<string, number> = { high: 0, medium: 1, low: 2 };
  return validated
    .sort((a, b) => urgencyRank[a.urgency] - urgencyRank[b.urgency])
    .slice(0, 5);
}

// ─── Provider ────────────────────────────────────────────────────────────────

export function FlowProvider({ children }: { children: React.ReactNode }) {
  const { contacts, updateStage, isLoading: contactsLoading } = useCRM();
  const { isAuthenticated, token } = useAuth();

  const inlineFlowAnchorRef = useRef<HTMLElement | null>(null);

  const [widgetState, setWidgetState] = useState<FlowWidgetState>('idle');
  const [isOpen, setIsOpen] = useState(false);
  const [panelAnchorSnapshot, setPanelAnchorSnapshot] = useState<CSSProperties | null>(null);
  const [suggestions, setSuggestions] = useState<FlowSuggestion[]>([]);
  const [currentDraft, setCurrentDraft] = useState<EmailDraft | null>(null);
  const [isDraftLoading, setIsDraftLoading] = useState(false);

  const suggestionsRef = useRef<FlowSuggestion[]>([]);
  suggestionsRef.current = suggestions;

  // Derive suggestions when CRM contact list is stable — read-only
  useEffect(() => {
    if (!isAuthenticated) {
      setSuggestions([]);
      return;
    }
    if (contactsLoading && contacts.length === 0) {
      return;
    }
    setSuggestions(deriveAmbientSuggestions(contacts));
  }, [contacts, isAuthenticated, contactsLoading]);

  // Keep panel mode aligned with the live queue (avoids empty suggestion UI or stale idle)
  useEffect(() => {
    if (currentDraft) return;
    if (suggestions.length === 0 && widgetState === 'suggestion') {
      setWidgetState('idle');
      return;
    }
    if (isOpen && suggestions.length > 0 && widgetState === 'idle') {
      setWidgetState('suggestion');
    }
  }, [isOpen, suggestions.length, widgetState, currentDraft]);

  const currentSuggestion = suggestions[0] ?? null;

  const openWidget = useCallback(() => {
    setPanelAnchorSnapshot(null);
    setIsOpen(true);
    setWidgetState(suggestions.length > 0 ? 'suggestion' : 'idle');
  }, [suggestions.length]);

  const openWidgetWithInlineAnchor = useCallback(() => {
    if (typeof window !== 'undefined') {
      const el = inlineFlowAnchorRef.current;
      setPanelAnchorSnapshot(
        computePanelAnchorStyle(el, window.innerWidth, window.innerHeight) ?? null
      );
    } else {
      setPanelAnchorSnapshot(null);
    }
    setIsOpen(true);
    setWidgetState(suggestions.length > 0 ? 'suggestion' : 'idle');
  }, [suggestions.length]);

  const closeWidget = useCallback(() => {
    setIsOpen(false);
    setPanelAnchorSnapshot(null);
    setWidgetState('idle');
    setCurrentDraft(null);
  }, []);

  // APPROVAL GATE — only fires after explicit user button click
  const approveSuggestion = useCallback(async () => {
    const suggestion = suggestions[0];
    if (!suggestion) return;

    // Validate contactId once more before dispatching
    if (!isValidContactId(suggestion.contactId)) {
      console.error('[Flow] Security: invalid contactId rejected at approval gate');
      setSuggestions((prev) => prev.slice(1));
      return;
    }

    if (suggestion.type === 'move_stage' && suggestion.targetStage) {
      // Delegate to useCRM — inherits auth token + error handling
      await updateStage(suggestion.contactId, suggestion.targetStage);
    }
    // follow_up and high_priority are read-only suggestions — no mutation

    setSuggestions((prev) => {
      const next = prev.slice(1);
      if (next.length === 0) {
        setWidgetState('idle');
        setIsOpen(false);
      }
      return next;
    });
  }, [suggestions, updateStage]);

  const dismissSuggestion = useCallback(() => {
    setSuggestions((prev) => {
      const next = prev.slice(1);
      if (next.length === 0) {
        setWidgetState('idle');
        setIsOpen(false);
      }
      return next;
    });
  }, []);

  const requestDraft = useCallback(async (contact: Contact) => {
    if (!isValidContactId(contact.id)) return;
    setIsDraftLoading(true);
    const draft = await emailDraftingService.generateDraft(contact, undefined, token);
    setIsDraftLoading(false);
    if (!draft) return;
    setCurrentDraft(draft);
    setWidgetState('drafting');
    setPanelAnchorSnapshot(null);
    setIsOpen(true);
  }, [token]);

  const approveDraft = useCallback(() => {
    if (!currentDraft) return;
    const text = `Subject: ${currentDraft.subject}\n\n${currentDraft.body}`;
    navigator.clipboard.writeText(text).catch(() => {});
    setCurrentDraft(null);
    const pending = suggestionsRef.current.length;
    setWidgetState(pending > 0 ? 'suggestion' : 'idle');
    if (pending === 0) setIsOpen(false);
  }, [currentDraft]);

  const dismissDraft = useCallback(() => {
    setCurrentDraft(null);
    const pending = suggestionsRef.current.length;
    setWidgetState(pending > 0 ? 'suggestion' : 'idle');
    if (pending === 0) setIsOpen(false);
  }, []);

  const getContact = useCallback(
    (id: string) => contacts.find((c) => c.id === id),
    [contacts]
  );

  return (
    <FlowContext.Provider
      value={{
        widgetState,
        isOpen,
        inlineFlowAnchorRef,
        panelAnchorSnapshot,
        currentSuggestion,
        pendingSuggestions: suggestions,
        suggestionsCount: suggestions.length,
        currentDraft,
        openWidget,
        openWidgetWithInlineAnchor,
        closeWidget,
        approveSuggestion,
        dismissSuggestion,
        requestDraft,
        isDraftLoading,
        approveDraft,
        dismissDraft,
        getContact,
      }}
    >
      {children}
    </FlowContext.Provider>
  );
}
